import { motion } from "framer-motion";
import { ArrowRight, Star } from "lucide-react";

const lines = ["Deine Website soll", "Kunden bringen —", "nicht nur existieren."];

const MaskedLine = ({ text, delay, accent }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className={`block ${accent ? "text-[#2563EB]" : ""}`}
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {text}
    </motion.span>
  </span>
);

const stats = [
  { value: "60+", label: "Projekte umgesetzt" },
  { value: "5,0", label: "Ø Google-Bewertung", icon: true },
  { value: "24h", label: "Antwortzeit garantiert" },
];

export default function Hero() {
  return (
    <section id="top" className="relative pt-32 md:pt-40 pb-16 md:pb-24 overflow-hidden">
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse 60% 50% at 70% 10%, rgba(37,99,235,0.07), transparent 70%)",
        }}
        aria-hidden="true"
      />
      <div className="max-w-6xl mx-auto px-5 md:px-8">
        <div className="max-w-3xl">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.6 }}
            className="font-mono text-xs uppercase tracking-[0.25em] text-[#2563EB] mb-6"
          >
            Webdesign für lokale Unternehmen
          </motion.p>
          <h1 className="font-display font-medium text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[1.05]">
            {lines.map((l, i) => (
              <MaskedLine key={l} text={l} delay={0.3 + i * 0.15} accent={i === 1} />
            ))}
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.7 }}
            className="mt-6 text-base md:text-lg leading-relaxed text-[#52525B] max-w-lg"
          >
            Ich baue moderne Webseiten für Handwerker, Praxen, Restaurants und andere lokale
            Betriebe — schnell, verständlich und darauf ausgelegt, dass Kunden dich finden und
            anfragen. Ohne Fachchinesisch, ohne versteckte Kosten.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.1, duration: 0.7 }}
            className="mt-8 flex flex-col sm:flex-row gap-3"
          >
            <a
              href="#kontakt"
              data-testid="hero-primary-cta"
              className="group inline-flex items-center justify-center gap-2 rounded-full bg-[#2563EB] text-white font-semibold px-7 py-3.5 hover:bg-[#1D4ED8] transition-colors duration-300 shadow-lg shadow-blue-600/25"
            >
              Kostenloses Erstgespräch sichern
              <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
            </a>
            <a
              href="#referenzen"
              data-testid="hero-secondary-cta"
              className="inline-flex items-center justify-center gap-2 rounded-full border border-[#E4E4E7] font-semibold px-7 py-3.5 text-[#0A0A0A] hover:border-[#0A0A0A] transition-colors duration-300"
            >
              Referenzen ansehen
            </a>
          </motion.div>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.4, duration: 0.8 }}
            className="mt-12 grid grid-cols-3 gap-4 max-w-md"
            data-testid="hero-trust-stats"
          >
            {stats.map((s) => (
              <div key={s.label} className="border-l border-[#E4E4E7] pl-4">
                <p className="font-mono font-semibold text-xl flex items-center gap-1">
                  {s.value}
                  {s.icon && <Star className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />}
                </p>
                <p className="text-xs text-[#A1A1AA] mt-1 leading-snug">{s.label}</p>
              </div>
            ))}
          </motion.div>
        </div>
      </div>
    </section>
  );
}
